import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ReactElementRenderer } from 'components/common/element-renderer';
import { LayoutContext } from 'components/layouts/LayoutContext';
import { CaseCitationTooltip } from 'components/cases/case/CaseCitationTooltip';
import { useContentHover } from 'components/cases/case/useContentHover';
import { markSearchTerms } from 'util/markSearchTerms';
import { applyFocusedClass } from 'util/applyFocusedClass';
import { applyHrefs } from 'util/applyHrefs';
import toElement from 'util/toElement';
import replace from 'lodash/fp/replace';
import { CitableDocumentProps } from 'components/citable-document';
import { CaseData } from 'types';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { ScrollTrack } from 'features/ScrollTracking';
import { StyledCase, StyledCaseContent } from './StyledCase';
import { FixedCaseHeaderSizeAtom } from './CaseHeader';
import { useReactElementTransformers } from 'components/common/element-renderer/useReactElementTransformers';
import {
  useTabbableCaseLinkTransformer,
  useTabbableStatuteLinkTransformer,
} from 'features/link-helpers/linkTransformers';

const { CitableDocument } =
  typeof window !== 'undefined'
    ? require('components/citable-document')
    : { CitableDocument: (props: CitableDocumentProps) => props.children };

const transformEmbeddedJpg = replace(
  new RegExp('<iframe src="(.*.jpg)".*?></iframe>', 'gi'),
  '<img src="$1" />',
);

function addCitingCasesSeparator(
  contentEl: HTMLElement | undefined,
  caseData: CaseData,
) {
  if (contentEl) {
    const citingSeparator = document.createElement('span');
    citingSeparator.textContent = `Citing Cases ${caseData.citationCount}`;
    const caseBody = contentEl.querySelector('.case-body');
    contentEl.querySelector('div')?.insertBefore(citingSeparator, caseBody!);
  }
}

export interface CaseSidebarHeading {
  headingId: string;
  title: string;
  element: any;
  level?: number;
}

export const caseHeadingsAtom = atom<CaseSidebarHeading[]>([]);
caseHeadingsAtom.debugLabel = 'CaseHeadings';

function useCaseTocTransformer() {
  const setCaseHeadingsSidebar = useSetAtom(caseHeadingsAtom);
  const location = useLocation();
  const headingsRef = useRef<CaseSidebarHeading[]>([]);

  useEffect(() => {
    headingsRef.current = [];
  }, [location.pathname]);

  const applySidebar = useCallback(() => {
    setCaseHeadingsSidebar(headingsRef.current);
  }, [setCaseHeadingsSidebar]);

  const caseTocTransformer = useCallback(
    (element: HTMLElement, children: React.ReactNode) => {
      let heading: undefined | CaseSidebarHeading = undefined;
      if (element.classList.contains('case-header')) {
        heading = {
          headingId: 'case-header',
          title: 'Header',
          level: 0,
          element,
        };
      }

      if (element.classList.contains('counsel')) {
        heading = {
          headingId: 'case-counsel',
          title: 'Counsel',
          level: 0,
          element,
        };
      }

      if (element.classList.contains('notes')) {
        heading = {
          headingId: 'case-notes',
          title: 'Footnotes',
          level: 0,
          element,
        };
      }

      if (
        ['H1', 'H2', 'H3', 'H4', 'H5'].includes(element.tagName) &&
        element.classList.contains('heading')
      ) {
        const level = Number(element.getAttribute('level'));
        let title: string | undefined = undefined;
        const headingId: string | undefined =
          element.id || element.firstElementChild?.id;

        if (element.classList.contains('opinion-heading')) {
          title =
            element.parentElement?.dataset &&
            element.parentElement?.dataset.title;
        } else {
          title =
            (element.dataset && element.dataset.title) ||
            element.getAttribute('data-title') ||
            element.getAttribute('value') ||
            element.textContent ||
            'Heading';
        }

        heading = {
          headingId: headingId!,
          title: title!,
          level,
          element,
        };
      }

      if (heading) {
        headingsRef.current = [
          ...headingsRef.current.filter(h => h.headingId !== heading.headingId),
          heading,
        ];

        return (
          <ScrollAdjustedHeader
            headingId={heading.headingId}
            as={element.tagName.toLowerCase()}
            className={element.className}
          >
            {children}
          </ScrollAdjustedHeader>
        );
      } else if (
        element.tagName === 'SUP' &&
        (element.classList.contains('notemarker') ||
          element.classList.contains('noteanchor'))
      ) {
        return (
          <ScrollAdjustedNotes className={element.className} element={element}>
            {children}
          </ScrollAdjustedNotes>
        );
      }

      return null;
    },
    [],
  );

  return [caseTocTransformer, applySidebar] as const;
}

function ScrollAdjustedHeader(props: {
  headingId: string;
  as: string;
  className?: string;
  children: React.ReactNode;
}) {
  const fixedHeaderSize = useAtomValue(FixedCaseHeaderSizeAtom);

  return (
    <ScrollTrack
      id={props.headingId}
      style={{ scrollMarginTop: `${fixedHeaderSize}px` }}
      as={props.as}
      className={props.className}
    >
      {props.children}
    </ScrollTrack>
  );
}

function ScrollAdjustedNotes({
  className,
  element,
  children,
}: {
  element: HTMLElement;
  className?: string;
  children: React.ReactNode;
}) {
  const fixedHeaderSize = useAtomValue(FixedCaseHeaderSizeAtom);

  return (
    <sup
      className={className}
      style={{ scrollMarginTop: `${fixedHeaderSize}px` }}
      data-ref={element.getAttribute('data-ref') || element.getAttribute('ref')}
      // eslint-disable-next-line react/no-unknown-property
      val={element.getAttribute('val')}
    >
      {children}
    </sup>
  );
}

export interface CaseContentDocumentProps {
  caseData: CaseData;
  onScroll?: () => void;
  query: string;
}

export const CaseContentDocument = React.forwardRef<
  HTMLDivElement,
  CaseContentDocumentProps
>(({ caseData, onScroll, query }, ref) => {
  const layout = React.useContext(LayoutContext);
  const { hash } = useLocation();
  const {
    judicataId,
    searchHighlightTerms,
    californiaCitationFormat,
    standardCitationFormat,
    content,
  } = caseData;

  const contentEl = React.useMemo(() => {
    const highlightedContent = markSearchTerms(searchHighlightTerms, content);
    const el = toElement(transformEmbeddedJpg(highlightedContent));
    addCitingCasesSeparator(el, caseData);
    applyFocusedClass(el, hash);
    applyHrefs(el);
    return el;
  }, [searchHighlightTerms, content, caseData, hash]);

  const [tooltipProps, onContentHover, closeTooltip] =
    useContentHover(onScroll);

  const [tocTransformer, applySidebar] = useCaseTocTransformer();
  const caseLinkTransformer = useTabbableCaseLinkTransformer();
  const statuteLinkTransformer = useTabbableStatuteLinkTransformer();

  const transformers = React.useMemo(() => {
    return [caseLinkTransformer, statuteLinkTransformer, tocTransformer];
  }, []);

  const caseContentTransformers = useReactElementTransformers(transformers);

  return (
    <StyledCase
      className="case-content-with-footer"
      ref={ref}
      onScroll={(e: React.UIEvent<HTMLDivElement>) => closeTooltip?.()}
      tabIndex={0}
      aria-label="Case Content"
      id={judicataId!}
    >
      <StyledCaseContent
        className="case-content-container"
        raw={layout?.raw}
        onMouseLeave={(e: React.UIEvent<HTMLDivElement>) => closeTooltip?.()}
        onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => onContentHover(e)}
        role="document"
      >
        <CitableDocument
          className="case-content"
          selectableSelector=".case-body"
          unspannableSelector=".note"
          unspannableParent=".notes"
          californiaCitationFormat={californiaCitationFormat}
          standardCitationFormat={standardCitationFormat}
        >
          {tooltipProps && (
            <CaseCitationTooltip
              {...tooltipProps}
              frameSelector=".case-body"
              close={
                (closeTooltip as unknown as React.MouseEventHandler<HTMLElement>) ||
                undefined
              }
            />
          )}
          <ReactElementRenderer
            content={contentEl as HTMLDivElement}
            transformer={caseContentTransformers}
            afterUpdate={applySidebar}
          />
        </CitableDocument>
      </StyledCaseContent>
    </StyledCase>
  );
});

CaseContentDocument.displayName = 'CaseContentDocument';
